import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ButtonIcon from '@glass/web/components/Button/ButtonIcon';
import CardHeader from '@glass/web/components/Card/CardHeader';
import CardActionArea from '@glass/web/components/base/CardActionArea';
import CardActions from '@glass/web/components/base/CardActions';
import Collapse from '@glass/web/components/base/Collapse';
import IconButton from '@glass/web/components/base/IconButton';
import LinearProgress from '@glass/web/components/base/LinearProgress';
import Paper from '@glass/web/components/base/Paper';
import ExpandMoreRounded from '@glass/web/components/base/icons/ExpandMoreRounded';
import SaveRounded from '@glass/web/components/base/icons/SaveRounded';
import { selectIsUser } from '@glass/web/modules/auth';
import {
  selectResumeIsDirty,
  selectResumeIsSubmitting,
} from '@glass/web/modules/resumes/selectors';
import resumeSize from '@glass/web/modules/resumes/templates/resumeSize';
import { MD } from '@glass/web/modules/theme/breakpointKeys';
import makeStyles from '@glass/web/modules/theme/makeStyles';

import AspectRatio from '@glass/shared/components/AspectRatio/AspectRatio';
import Button from '@glass/shared/components/Button/Button';
import ResumePreview from '@glass/shared/components/ResumeBuilder/ResumePreview';
import Rotate from '@glass/shared/components/Rotate/Rotate';
import useResumeProgress from '@glass/shared/components/hooks/useResumeProgress';
import useResumeWords from '@glass/shared/modules/content/staticVariations/resumeWords/useResumeWords';
import promptSaveProgressAction from '@glass/shared/modules/resumes/actions/promptSaveProgressAction';
import useMergePlaceholderResume from '@glass/shared/modules/resumes/hooks/useMergePlaceholderResume';
import useSaveResumeProgress from '@glass/shared/modules/resumes/hooks/useSaveResumeProgress';

const useStyles = makeStyles()((theme) => ({
  header: {
    padding: theme.spacing(1),
  },
  cardActions: {
    paddingTop: 0,
  },
  hiddenMdDn: {
    [theme.breakpoints.down(MD)]: {
      display: 'none',
    },
  },
  hiddenMdUp: {
    [theme.breakpoints.up(MD)]: {
      display: 'none',
    },
  },
  preview: {
    marginTop: theme.spacing(1),
  },
}));

function ResumeStatus({
  className,
  resume,
  activeSection,
  disablePreview,
  actions,
  cardActions,
  loading,
  disableSaveToInbox,
  iSlug,
  jSlug,
  seedOverride,
  skip,
}) {
  const [open, setOpen] = useState(false);
  const isUser = useSelector(selectIsUser);
  const { name } = resume || {};
  const { count } = useResumeProgress(resume);
  const { classes } = useStyles();
  const submitting = useSelector(selectResumeIsSubmitting);
  const dirty = useSelector(selectResumeIsDirty);
  const handleClick = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const {
    resume: resumeWithPlaceholder,
    sectionClasses,
    loading: loadingMerge,
  } = useMergePlaceholderResume({
    resume,
    iSlug,
    jSlug,
    seedOverride,
    skip,
  });

  const resumeId = resume?.id;
  const enabled = useMemo(
    () => !disableSaveToInbox && !loading && !!(!submitting && (resumeId || dirty)),
    [disableSaveToInbox, loading, submitting, resumeId, dirty],
  );
  const dispatch = useDispatch();

  useSaveResumeProgress({
    skip: !enabled,
    preload: enabled,
    resumeId,
  });

  const handleSaveToInbox = useCallback(
    (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      dispatch(promptSaveProgressAction({ resumeId, reason: 'click' }));
    },
    [resumeId, dispatch],
  );

  const resumePreview = useMemo(() => {
    if (disablePreview) {
      return null;
    }
    return (
      <Paper square className={classes.preview} elevation={1}>
        <AspectRatio height={resumeSize.height} width={resumeSize.width}>
          <ResumePreview
            singlePage
            activeSection={activeSection}
            loading={loading || loadingMerge}
            resume={resumeWithPlaceholder}
            sectionClasses={sectionClasses}
          />
        </AspectRatio>
        {actions ? <CardActions>{actions}</CardActions> : null}
      </Paper>
    );
  }, [
    actions,
    activeSection,
    classes.preview,
    disablePreview,
    loading,
    loadingMerge,
    resumeWithPlaceholder,
    sectionClasses,
  ]);

  const resumeWords = useResumeWords();
  const isSave = !!resumeId;

  const inner = (
    <>
      <LinearProgress className={classes.progress} value={count} variant="determinate" />
      <CardHeader
        action={
          !disablePreview ? (
            <IconButton as="div" size="large" sx={{ display: { xs: 'flex', md: 'none' } }}>
              <Rotate component={ExpandMoreRounded} rotate={open ? 180 : 0} />
            </IconButton>
          ) : null
        }
        className={classes.header}
        subheader={`${count}% progress`}
        title={name || `My ${resumeWords.capitalized}`}
      />
      {cardActions || (!disableSaveToInbox && isSave) ? (
        <CardActions className={classes.cardActions}>
          {cardActions}
          {!disableSaveToInbox && isSave ? (
            <Button
              color={isUser ? 'inherit' : 'secondary'}
              size="small"
              variant={isUser ? 'text' : 'contained'}
              onClick={handleSaveToInbox}
            >
              <ButtonIcon fontSize={16} icon={SaveRounded} margin={0.5} />
              Save to Inbox
            </Button>
          ) : null}
        </CardActions>
      ) : null}
    </>
  );

  return (
    <div className={className}>
      <Paper variant="outlined">
        <div className={classes.hiddenMdDn}>{inner}</div>
        <div className={classes.hiddenMdUp}>
          {!disablePreview ? (
            <CardActionArea component="a" onClick={handleClick}>
              {inner}
            </CardActionArea>
          ) : (
            inner
          )}
        </div>
      </Paper>
      {disablePreview ? null : (
        <>
          <div className={classes.hiddenMdUp}>
            <Collapse in={open}>{resumePreview}</Collapse>
          </div>
          <div className={classes.hiddenMdDn}>{resumePreview}</div>
        </>
      )}
    </div>
  );
}

ResumeStatus.defaultProps = {
  disableSaveToInbox: false,
};

ResumeStatus.propTypes = {
  actions: PropTypes.node,
  activeSection: PropTypes.string,
  cardActions: PropTypes.node,
  className: PropTypes.string,
  disablePreview: PropTypes.bool,
  disableSaveToInbox: PropTypes.bool,
  iSlug: PropTypes.string,
  jSlug: PropTypes.string,
  loading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  resume: PropTypes.object,
  seedOverride: PropTypes.string,
  skip: PropTypes.bool,
};

ResumeStatus.displayName = 'ResumeStatus';

export default React.memo(ResumeStatus);
